import React, { useState, useCallback, useRef, useLayoutEffect, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import AgencyAPIs from "../../../apiRoutes/Agency";
import CustomModal from "../../common/CustomModal";
import { format } from "date-fns";
import SearchableSelect from "../../common/SearchableSelect";
import MultiSelectCell from "../../common/MultiSelectCell";
import TwoLevelCell from "../../common/TwoLevelCell";
import { DatePicker } from "../../ui/date-picker";
import useCtmOptionsByApp from "../../../hooks/useCtmOptionsByApp";
import { composeModule } from "../../../utils/adNameBuilder";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import AdsAPIs from "../../../apiRoutes/Ads";
import { GET_AGENCIES_QUERY_KEY, GET_AD_REQUESTS_QUERY_KEY, GET_AD_DELIVERIES_QUERY_KEY } from "../../../utils/constants/queryKeys";
import { ACCOUNT_OPTIONS, AUTO_LAUNCH_OPTIONS, DUE_DATE_END_MONTH } from "../../../utils/constants/AdRequest";
import { handleParseAutoLaunch } from "../../../utils/helperFunctions";
import useAmsConfig from "../../../hooks/useAmsConfig";
import { STICKY_ADSET_CELL, STICKY_ADSET_HEAD, columnWidth } from "../utils/gridLayout";
import toast from "react-hot-toast";
import useZustandStore from "../../../zustandStore/useZustandStore";
import usePermissions from "../../../hooks/usePermissions";
import { AGENCY_LIST_PERMISSIONS } from "../../../utils/constants/permissions";
import { agencyDisplayName, getAgencyIdFromUser, isAgencyScopedUser } from "../../../utils/agencyScope";
import { clipboardGrid } from "../../../utils/clipboard";
import {
  DIRECT_DELIVERY_KEYS,
  DIRECT_OPTIONAL_KEYS,
  directColumns,
  isUnusedHookCell,
  visibleColumns,
  directDeliveryFields,
  directErrorMessage,
  directRowProblem,
  handleGridAdsetName,
} from "../utils/directRows";

const PRIORITY_OPTIONS = ["low", "medium", "high"];
// How many hooks (= ads) one request can ask for; blank means 1.
const HOOKS_COUNT_MIN = 1;
const HOOKS_COUNT_MAX = 10;
const HOOKS_COUNT_OPTIONS = Array.from(
  { length: HOOKS_COUNT_MAX - HOOKS_COUNT_MIN + 1 },
  (_, i) => String(HOOKS_COUNT_MIN + i)
);
const DATA_KEYS = ["for_app_name", "language", "account", "category", "script_writer_name", "track", "track_number", "topic", "script_name", "module_type", "module", "creative_format", "visual_format", "human_ai", "script_type", "hooks_count", "priority", "due_delivery_date", "script", "comments", "reference_link", "auto_launch"];
const COLUMN_LABELS = ["App *", "Language *", "Account *", "Category *", "Script Writer", "Track *", "Track #", "Topic *", "Script Name *", "Module Type *", "Module *", "Creative Format *", "Visual Format *", "Human/AI *", "Script Type *", "Hooks", "Priority", "Due date *", "Script *", "Comments", "Reference Link", "Auto launch"];
const SELECT_COLUMNS = { priority: PRIORITY_OPTIONS, hooks_count: HOOKS_COUNT_OPTIONS, auto_launch: AUTO_LAUNCH_OPTIONS };
const REQUIRED_KEYS = ["for_app_name", "language", "account", "category", "track", "topic", "script_name", "module_type", "module", "creative_format", "visual_format", "human_ai", "script_type", "due_delivery_date", "script"];
const LONG_TEXT_KEYS = new Set(["script", "comments"]);
const DIRECT = directColumns(DATA_KEYS, COLUMN_LABELS);
const DIRECT_REQUIRED_KEYS = REQUIRED_KEYS.filter((k) => DIRECT.keys.includes(k) && !DIRECT_OPTIONAL_KEYS.has(k));
const DEFAULT_ROW_COUNT = 25;

const EMPTY_ROW_TEMPLATE = createEmptyRow();

// remarks has no column of its own but is editable, so it counts as data.
const EMPTINESS_KEYS = [...DATA_KEYS, ...DIRECT_DELIVERY_KEYS, "remarks"];

const isRowEmpty = (row) =>
  EMPTINESS_KEYS.every(
    (k) => (row[k] ?? "").toString().trim() === (EMPTY_ROW_TEMPLATE[k] ?? "").toString().trim()
  );

function createEmptyRow() {
  return {
    ...Object.fromEntries([...DATA_KEYS, ...DIRECT_DELIVERY_KEYS].map((k) => [k, k === "priority" ? "low" : ""])),
    remarks: "",
    remarks_edited: false,
  };
}

function AccountCell({ row, rowIndex, updateCell }) {
  return (
    <SearchableSelect
      value={row.account}
      options={ACCOUNT_OPTIONS}
      onChange={(v) => updateCell(rowIndex, "account", v)}
    />
  );
}

const UploadExcelModal = ({ open, onClose, direct = false }) => {
  const cfg = useAmsConfig();
  // Direct mode: no hooks/priority/due date columns; requester, links, ad type, creator, editor added.
  const [rows, setRows] = useState(() => Array.from({ length: DEFAULT_ROW_COUNT }, createEmptyRow));
  // Direct grid shows hook link columns up to the largest Hooks value picked.
  const shown = direct ? visibleColumns(DIRECT.keys, DIRECT.labels, rows) : null;
  const keys = direct ? shown.keys : DATA_KEYS;
  // Hidden hook columns are always the trailing ones, so pasting follows the full layout.
  const allKeys = direct ? DIRECT.keys : DATA_KEYS;
  const labels = direct ? shown.labels : COLUMN_LABELS;
  const requiredKeys = direct ? DIRECT_REQUIRED_KEYS : REQUIRED_KEYS;
  const title = direct ? "Deliver without request (bulk)" : "Bulk Create Ad Requests";
  const [agencyId, setAgencyId] = useState("");
  const [comments, setComments] = useState("");
  const [selectedRowIndices, setSelectedRowIndices] = useState([]);
  const [showDataModal, setShowDataModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const tableRef = useRef(null);
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();
  const currentUser = useZustandStore((s) => s?.currentUser);
  const scopedAgencyId = getAgencyIdFromUser(currentUser);
  const agencyScoped = isAgencyScopedUser(currentUser);
  const { hasPermission } = usePermissions();
  const canListAgencies = AGENCY_LIST_PERMISSIONS.some((p) => hasPermission(p));
  const { getCategories, getTracks, getTrackNumber } = useCtmOptionsByApp({ enabled: open });

  const { data: agenciesResult, isLoading: agenciesLoading } = useQuery({
    queryKey: GET_AGENCIES_QUERY_KEY,
    queryFn: () => AgencyAPIs.list({ axiosPrivate, status: "approved", limit: 100, offset: 0 }),
    enabled: open && !agencyScoped && canListAgencies,
  });
  const agencies = (agenciesResult?.data ?? []).filter((a) => !direct || a.allow_direct_delivery);
  const agencyName =
    agencies.find((a) => Number(a.id) === Number(agencyId))?.name || currentUser?.agency_name || "";

  const { data: adTypesResult } = useQuery({
    queryKey: ["adtypes-for-agency", agencyId ? Number(agencyId) : null],
    queryFn: () => AdsAPIs.listAdTypesForAgency({ axiosPrivate, agency_id: Number(agencyId) }),
    enabled: open && direct && !!agencyId,
  });
  const adTypeNames = (adTypesResult?.data ?? []).map((t) => t.name);

  useLayoutEffect(() => {
    if (!open) {
      setShowDataModal(false);
      setAgencyId("");
      return;
    }
    if (agencyScoped && scopedAgencyId != null) {
      setAgencyId(String(scopedAgencyId));
      setShowDataModal(true);
    } else if (!agencyScoped) {
      setShowDataModal(false);
    }
  }, [open, agencyScoped, scopedAgencyId]);

  const handleNext = () => {
    if (!agencyId) return toast.error("Select an agency.");
    setRows(Array.from({ length: DEFAULT_ROW_COUNT }, createEmptyRow));
    setSelectedRowIndices([]);
    setShowDataModal(true);
  };

  const handleSubmit = async () => {
    if (!agencyId) return toast.error("Select an agency.");
    const selectedRows = rows.filter((_, i) => selectedRowIndices.includes(i));
    const nonEmpty = selectedRows.filter((r) => !isRowEmpty(r));
    if (!nonEmpty.length) return toast.error("No rows with data to submit.");
    for (const r of nonEmpty) {
      const gap = requiredKeys.find((k) => !(r[k] ?? "").toString().trim());
      if (gap) return toast.error(`Row ${rows.indexOf(r) + 1}: ${labels[keys.indexOf(gap)].replace(" *", "")} is required.`);
      const problem = direct ? directRowProblem(r) : null;
      if (problem) return toast.error(`Row ${rows.indexOf(r) + 1}: ${problem}`);
    }
    const badHooks = nonEmpty.find((r) => {
      const raw = (r.hooks_count ?? "").toString().trim();
      if (raw === "") return false;
      const n = Number(raw);
      return !Number.isInteger(n) || n < HOOKS_COUNT_MIN || n > HOOKS_COUNT_MAX;
    });
    if (badHooks)
      return toast.error(
        `Hooks must be a whole number between ${HOOKS_COUNT_MIN} and ${HOOKS_COUNT_MAX} (blank = 1).`
      );
    const payloads = nonEmpty.map((row) => {
      return {
        requested_for_agency_id: Number(agencyId),
        topic: row.topic || undefined,
        due_delivery_date: row.due_delivery_date || undefined,
        priority: row.priority || "low",
        comments: row.comments || comments || undefined,
        requested_via: "bulk_entry",
        for_app_name: row.for_app_name || undefined,
        language: row.language || undefined,
        account: row.account || undefined,
        category: row.category || undefined,
        script_writer_name: row.script_writer_name || undefined,
        track: row.track || undefined,
        track_number: (row.track_number ?? "").toString().trim() !== "" ? Number(row.track_number) : undefined,
        hooks_count: (row.hooks_count ?? "").toString().trim() !== "" ? Number(row.hooks_count) : undefined,
        script_name: row.script_name || undefined,
        module_type: row.module_type || undefined,
        module: row.module || undefined,
        creative_format: row.creative_format || undefined,
        visual_format: row.visual_format || undefined,
        human_ai: row.human_ai || undefined,
        script_type: row.script_type || undefined,
        script: row.script || undefined,
        reference_link: row.reference_link || undefined,
        remarks: row.remarks_edited ? row.remarks?.trim() || undefined : undefined,
        auto_launch: handleParseAutoLaunch({ value: row.auto_launch }),
      };
    });
    if (direct) return submitDirect(nonEmpty, payloads);
    setIsSubmitting(true);
    try {
      const result = await AdsAPIs.bulkCreateAdRequests({ axiosPrivate, requests: payloads });
      const created = result?.count ?? payloads.length;
      queryClient.invalidateQueries({ queryKey: GET_AD_REQUESTS_QUERY_KEY });
      toast.success(`${created} ad request(s) created.`);
      handleClose();
    } catch (_) {
      toast.error("Bulk submit failed.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // All rows or none: the backend saves nothing if any row fails and returns that row's error.
  const submitDirect = async (sourceRows, payloads) => {
    setIsSubmitting(true);
    try {
      const items = payloads.map(({ requested_for_agency_id, requested_via, due_delivery_date, priority, hooks_count, ...brief }, i) => ({
        ...brief,
        ...directDeliveryFields(sourceRows[i]),
      }));
      const result = await AdsAPIs.createDirectAdRequests({
        axiosPrivate,
        payload: { ...(agencyScoped ? {} : { agency_id: Number(agencyId) }), items },
      });
      queryClient.invalidateQueries({ queryKey: GET_AD_REQUESTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: GET_AD_DELIVERIES_QUERY_KEY });
      toast.success(`${result?.count ?? items.length} request(s) created, ${result?.delivery_count ?? 0} hook(s) sent to moderation.`);
      handleClose();
    } catch (err) {
      toast.error(directErrorMessage(err, sourceRows.map((r) => rows.indexOf(r) + 1)));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setAgencyId("");
    setComments("");
    setRows(Array.from({ length: DEFAULT_ROW_COUNT }, createEmptyRow));
    setShowDataModal(false);
    setSelectedRowIndices([]);
    onClose();
  };

  const toggleRow = (i) => {
    setSelectedRowIndices((prev) =>
      prev.includes(i) ? prev.filter((j) => j !== i) : [...prev, i].sort((a, b) => a - b)
    );
  };

  const rowsWithData = rows.reduce((acc, row, i) => (isRowEmpty(row) ? acc : [...acc, i]), []);

  const toggleSelectAll = () => {
    setSelectedRowIndices((prev) => (prev.length === rowsWithData.length ? [] : rowsWithData));
  };

  const updateCell = useCallback((rowIndex, key, value) => {
    setRows((prev) => {
      const updated = prev.map((r, idx) => {
        if (idx !== rowIndex) return r;
        const newRow = { ...r, [key]: value };
        if (key === "remarks") newRow.remarks_edited = true;
        if (key === "for_app_name" || key === "language") {
          // App and language drive the CTM cascade; reset downstream selections.
          if (key === "for_app_name") newRow.language = "";
          newRow.category = "";
          newRow.track = "";
          newRow.track_number = "";
        } else if (key === "category") {
          newRow.track = "";
          newRow.track_number = "";
        } else if (key === "track") {
          newRow.track_number = getTrackNumber(newRow.for_app_name, newRow.language, newRow.category, value);
        } else if (key === "module_type") {
          newRow.module = "";
        }
        return newRow;
      });
      return updated;
    });
  }, [getTrackNumber]);

  // Empty -> filled selects the row; filled -> empty deselects it.
  const prevRowsRef = useRef(rows);
  useEffect(() => {
    const prev = prevRowsRef.current;
    prevRowsRef.current = rows;
    setSelectedRowIndices((sel) => {
      let next = sel;
      rows.forEach((row, i) => {
        const wasEmpty = !prev[i] || isRowEmpty(prev[i]);
        const nowEmpty = isRowEmpty(row);
        if (wasEmpty && !nowEmpty && !next.includes(i)) next = [...next, i];
        else if (!wasEmpty && nowEmpty && next.includes(i)) next = next.filter((j) => j !== i);
      });
      return next === sel ? sel : [...next].sort((a, b) => a - b);
    });
  }, [rows]);

  // Auto-match: backfill empty track_number for rows whose (app, language, category, track)
  // matches a CTM mapping. Covers pasted rows; non-matching text is left untouched.
  useEffect(() => {
    setRows((prev) => {
      let changed = false;
      const next = prev.map((r) => {
        const current = (r.track_number ?? "").toString().trim();
        if (current !== "" || !r.for_app_name || !r.language || !r.category || !r.track) return r;
        const num = getTrackNumber(r.for_app_name, r.language, r.category, r.track);
        if (!num) return r;
        changed = true;
        return { ...r, track_number: num };
      });
      return changed ? next : prev;
    });
  }, [getTrackNumber]);

  const addRow = () => {
    setRows((prev) => [...prev, createEmptyRow()]);
    setSelectedRowIndices((prev) => [...prev, rows.length]);
  };

  const handleTablePaste = useCallback((e) => {
    const pastedGrid = clipboardGrid(e.clipboardData);
    if (!pastedGrid) return;

    const target = e.target;
    const td = target.closest("td");
    if (!td) return;
    const tr = td.closest("tr");
    if (!tr) return;

    const tbody = tr.closest("tbody");
    if (!tbody) return;
    const allRows = Array.from(tbody.querySelectorAll("tr"));
    const startRowIdx = allRows.indexOf(tr);

    const tds = Array.from(tr.querySelectorAll("td"));
    const startColIdx = tds.indexOf(td) - 1;

    e.preventDefault();

    setRows((prev) => {
      const next = [...prev];
      const totalRowsNeeded = startRowIdx + pastedGrid.length;
      while (next.length < totalRowsNeeded) {
        next.push(createEmptyRow());
      }
      for (let r = 0; r < pastedGrid.length; r++) {
        for (let c = 0; c < pastedGrid[r].length; c++) {
          const rowIdx = startRowIdx + r;
          const colIdx = startColIdx + c;
          if (colIdx >= 0 && colIdx < allKeys.length) {
            next[rowIdx] = { ...next[rowIdx], [allKeys[colIdx]]: pastedGrid[r][c] };
          }
        }
      }
      return next;
    });

    setSelectedRowIndices((prev) => {
      const newIndices = new Set(prev);
      for (let r = 0; r < pastedGrid.length; r++) {
        newIndices.add(startRowIdx + r);
      }
      return Array.from(newIndices).sort((a, b) => a - b);
    });
  }, [allKeys]);

  if (!open) return null;

  return (
    <>
    {!showDataModal && !agencyScoped && canListAgencies && (
      <CustomModal open={open} title={title} onClose={handleClose} isButtonsRequired={false}>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>
              Agency <span className="text-red-500">*</span>
            </Label>
            <Select value={agencyId} onValueChange={setAgencyId} required disabled={agenciesLoading}>
              <SelectTrigger>
                <SelectValue placeholder="Select agency" />
              </SelectTrigger>
              <SelectContent>
                {(agencies ?? []).map((a, i) => (
                  <SelectItem key={a.id} value={String(a.id)}>
                    {agencyDisplayName(a, i)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="button" onClick={handleNext} disabled={!agencyId}>
              Next
            </Button>
          </div>
        </div>
      </CustomModal>
    )}
    {!showDataModal && !agencyScoped && !canListAgencies && (
      <CustomModal open={open} title={title} onClose={handleClose} isButtonsRequired={false}>
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          You don’t have permission to list agencies. Ask an admin to grant agency management, or use an agency-linked account.
        </p>
        <div className="flex justify-end pt-4">
          <Button type="button" variant="outline" onClick={handleClose}>
            Close
          </Button>
        </div>
      </CustomModal>
    )}

    {showDataModal && (
      <CustomModal
        open={showDataModal}
        title={`${rows.length} Rows, ${selectedRowIndices.length} Selected`}
        onClose={() => (agencyScoped ? handleClose() : setShowDataModal(false))}
        isButtonsRequired={false}
        className="!w-[95%] !max-w-7xl md:!w-[92%] lg:!w-[90%]"
      >
        <div className="max-h-[65vh] overflow-auto pb-3" ref={tableRef} onPaste={handleTablePaste}>
          <table className="w-full text-left text-sm border-collapse border border-gray-200">
            <thead className="sticky top-0 bg-white z-10">
              <tr>
                <th className="py-2 px-2 w-10 border border-gray-200">
                  <input
                    type="checkbox"
                    checked={selectedRowIndices.length === rowsWithData.length && rowsWithData.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300"
                  />
                </th>
                {keys.map((key, idx) => {
                  const label = labels[idx];
                  return (
                    <th key={key} className={`py-2 px-2 font-medium text-gray-600 whitespace-nowrap border border-gray-200 ${columnWidth(key)}`}>
                      {label.endsWith("*") ? (
                        <>{label.slice(0, -1).trim()} <span className="text-red-500">*</span></>
                      ) : label}
                    </th>
                  );
                })}
                <th className={`py-2 px-2 font-medium text-gray-600 whitespace-nowrap border border-gray-200 ${STICKY_ADSET_HEAD}`}>
                  Ad set name
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className="hover:bg-gray-50/50">
                  <td className="py-1 px-2 w-10 border border-gray-200">
                    <input
                      type="checkbox"
                      checked={selectedRowIndices.includes(i)}
                      onChange={() => toggleRow(i)}
                      className="rounded border-gray-300"
                    />
                  </td>
                  {keys.map((key) => (
                    <td key={key} className={`py-0 px-0 border border-gray-200 ${columnWidth(key)}`}>
                      {key === "account" ? (
                        <AccountCell row={row} rowIndex={i} updateCell={updateCell} />
                      ) : key === "due_delivery_date" ? (
                        <DatePicker
                          value={row.due_delivery_date}
                          onChange={(d) => updateCell(i, "due_delivery_date", d ? format(d, "yyyy-MM-dd") : "")}
                          endMonth={DUE_DATE_END_MONTH}
                          className="h-8 min-w-[150px] rounded-none border-0 px-2 text-sm shadow-none focus-visible:ring-inset"
                        />
                      ) : key === "category" ? (
                        <SearchableSelect
                          value={row.category ?? ""}
                          options={getCategories(row.for_app_name, row.language)}
                          onChange={(v) => updateCell(i, "category", v)}
                        />
                      ) : key === "track" ? (
                        <SearchableSelect
                          value={row.track ?? ""}
                          options={getTracks(row.for_app_name, row.language, row.category).map((t) => t.track)}
                          onChange={(v) => updateCell(i, "track", v)}
                        />
                      ) : key === "track_number" ? (
                        <input
                          value={row.track_number ?? ""}
                          readOnly
                          className="w-full min-w-0 bg-gray-50 py-1.5 px-2 text-sm text-gray-500"
                        />
                      ) : key === "for_app_name" ? (
                        <SearchableSelect value={row.for_app_name ?? ""} options={cfg.appNames.map((a) => a.value)} onChange={(v) => updateCell(i, "for_app_name", v)} />
                      ) : key === "language" ? (
                        <SearchableSelect value={row.language ?? ""} options={cfg.languagesFor(row.for_app_name).map((l) => l.value)} onChange={(v) => updateCell(i, "language", v)} />
                      ) : key === "module_type" ? (
                        <SearchableSelect value={row.module_type ?? ""} options={cfg.moduleTypes.map((m) => m.value)} onChange={(v) => updateCell(i, "module_type", v)} />
                      ) : key === "human_ai" ? (
                        <SearchableSelect value={row.human_ai ?? ""} options={cfg.humanAi.map((o) => o.value)} onChange={(v) => updateCell(i, "human_ai", v)} />
                      ) : key === "script_type" ? (
                        <SearchableSelect value={row.script_type ?? ""} options={cfg.scriptTypes.map((o) => o.value)} onChange={(v) => updateCell(i, "script_type", v)} />
                      ) : key === "creative_format" ? (
                        <TwoLevelCell value={row.creative_format ?? ""} bases={cfg.creativeFormats} variationsFor={cfg.variationsFor} placeholder="Pick creative format" onChange={(v) => updateCell(i, "creative_format", v)} />
                      ) : key === "visual_format" ? (
                        <TwoLevelCell value={row.visual_format ?? ""} bases={cfg.visualFormats} variationsFor={cfg.visualVariationsFor} placeholder="Pick visual format" onChange={(v) => updateCell(i, "visual_format", v)} />
                      ) : key === "module" ? (
                        <MultiSelectCell
                          values={String(row.module ?? "").split("x").filter(Boolean)}
                          options={cfg.modulesFor(row.module_type)}
                          disabled={!row.module_type}
                          placeholder={row.module_type ? "Pick modules" : "Pick module type first"}
                          onChange={(vals) => updateCell(i, "module", composeModule(vals, cfg.modulesFor(row.module_type).map((m) => m.value)))}
                        />
                      ) : key === "ad_type" ? (
                        <SearchableSelect value={row.ad_type ?? ""} options={adTypeNames} onChange={(v) => updateCell(i, "ad_type", v)} />
                      ) : direct && isUnusedHookCell(row, key) ? (
                        <input disabled className="w-full min-w-0 cursor-not-allowed bg-gray-100 py-1.5 px-2 text-sm" />
                      ) : SELECT_COLUMNS[key] ? (
                        <SearchableSelect
                          value={row[key] ?? ""}
                          options={SELECT_COLUMNS[key]}
                          onChange={(v) => updateCell(i, key, v)}
                        />
                      ) : LONG_TEXT_KEYS.has(key) ? (
                        <textarea
                          value={row[key] ?? ""}
                          onChange={(e) => updateCell(i, key, e.target.value)}
                          rows={2}
                          className="w-full min-w-0 bg-transparent py-1.5 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-inset resize-y"
                        />
                      ) : (
                        <input
                          value={row[key] ?? ""}
                          onChange={(e) => updateCell(i, key, e.target.value)}
                          className="w-full min-w-0 bg-transparent py-1.5 px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-inset"
                        />
                      )}
                    </td>
                  ))}
                  <td className={`py-0 px-0 border border-gray-200 align-top ${STICKY_ADSET_CELL}`}>
                    <textarea
                      rows={2}
                      value={
                        row.remarks_edited
                          ? row.remarks
                          : handleGridAdsetName({ row, agencyName, direct })
                      }
                      onChange={(e) => updateCell(i, "remarks", e.target.value)}
                      className="block w-full min-w-0 resize-none break-all bg-transparent py-1.5 px-2 font-mono text-[11px] leading-4 focus:outline-none focus:bg-blue-50/40"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="border-t border-gray-200 pt-3 flex items-center justify-between mt-2">
          <Button type="button" variant="outline" size="sm" onClick={addRow}>
            + Add row
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => (agencyScoped ? handleClose() : setShowDataModal(false))}>
              Back
            </Button>
            <Button type="button" onClick={handleSubmit} disabled={isSubmitting || selectedRowIndices.length === 0}>
              {isSubmitting ? "Creating…" : `${direct ? "Create & deliver" : "Create"} (${selectedRowIndices.length})`}
            </Button>
          </div>
        </div>
      </CustomModal>
    )}
    </>
  );
};

export default UploadExcelModal;
